import { IonButton, IonCard, IonCardContent, IonCardHeader, IonIcon, IonRow } from '@ionic/react';
import { informationCircle } from 'ionicons/icons';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { JobMessages } from 'src/intl/JobMessages';
import { TimeTable } from 'src/utils/logs/logHelpers';
import { JobLogV2Data } from 'src/utils/logs/types';
import { DqText } from '../dq-text';
import HighPressureModal from '../job-details/information/information-modals/HighPressureModal';
import { Range, RangeSet } from './utils';
import { PieChart } from './wrappers/PieChart';
import { PieChartDataEntry } from './wrappers/PieChartController';

export interface PiePressureInfo {
  title: string;
  valve3: JobLogV2Data['valveStatus'][number];
  valve16: JobLogV2Data['valveStatus'][number];
  timeRange: { min: number; max: number };
  /** Time range based on job ms elapsed */
  timeTable: JobLogV2Data['timeTable'];
  showTimeDetails: boolean;
  controllerOffRanges: RangeSet;
  /** Time range based on calendar time if FW v >= 2.3.18, otherwise based on job ms elapsed and matches timeRange */
  logFileTimeRangeMin: number;
  logFileTimeRangeMax: number;
}

export default function PieHighPressure({
  title,
  valve3,
  valve16,
  timeRange,
  timeTable,
  showTimeDetails,
  controllerOffRanges,
  logFileTimeRangeMin,
  logFileTimeRangeMax,
}: PiePressureInfo) {
  const { formatMessage } = useIntl();
  const [helpDialogOpen, setHelpDialogOpen] = useState<boolean>(false);

  const time = useMemo(() => {
    if (!timeTable?.length) return undefined;
    return TimeTable.from(timeTable);
  }, [timeTable]);

  const highPressureEvents = useMemo(() => {
    return [valve3.valveA, valve3.valveB, valve16.valveA, valve16.valveB].flat();
  }, [valve16.valveA, valve16.valveB, valve3.valveA, valve3.valveB]);

  const highPressureRange = useMemo<RangeSet>(() => {
    const set = new RangeSet(logFileTimeRangeMin, Math.max(logFileTimeRangeMin + 1, logFileTimeRangeMax));
    try {
      set.addRange(...highPressureEvents.map(Range.fromValve));
    } catch (err) {
      console.warn('Error parsing high pressure range for high pressure pie chart.');
    }
    return set;
  }, [highPressureEvents, logFileTimeRangeMax, logFileTimeRangeMin]);

  /** The duration in ms that the job took, counting only moments while the controller was on. */
  const totalRuntime = timeRange.max - timeRange.min;

  /** The duration in ms that the job took in real (calendar) time. */
  const totalCalendarTime = useMemo(() => {
    if (!time) return 0;
    return time.getInstant(logFileTimeRangeMax) - time.getInstant(logFileTimeRangeMin);
  }, [time, logFileTimeRangeMax, logFileTimeRangeMin]);

  // There is evidently an issue with the real-time clock on some controllers that
  // causes the calculated controller off time to be negative. To address this,
  // we ensure that the controller off time is never less than zero. Note that
  // this will mask the case where the controller was off for a period of time,
  // but this is acceptable from a business perspecitve.
  const controllerOffTime = Math.max(totalCalendarTime - totalRuntime, 0);

  /** For Controller FW >= 2.3.18: Get all times controller was off during the range so we can exclude it from the high pressure off time */
  const filteredControllerOffRanges = useMemo(() => {
    const set = new RangeSet(logFileTimeRangeMin, Math.max(logFileTimeRangeMin + 1, logFileTimeRangeMax));
    try {
      set.addRange(...controllerOffRanges.getRangesBeforePointInTime(logFileTimeRangeMax));
    } catch (err) {
      console.warn('Error parsing off range for system pie chart.', err);
    }
    return set;
  }, [controllerOffRanges, logFileTimeRangeMin, logFileTimeRangeMax]);

  const chartInfo = useMemo(() => {
    const hpTime = highPressureRange.sumIncluded();
    const hpOffTime = highPressureRange.union(filteredControllerOffRanges).sumExcluded();

    const dataArray: PieChartDataEntry[] = [];
    dataArray.push({
      key: 'on',
      label: formatMessage(JobMessages.highPressureOnLegend),
      value: showTimeDetails ? hpTime : (hpTime / totalCalendarTime) * 100,
      color: 'data-blue',
    });
    dataArray.push({
      key: 'off',
      label: formatMessage(JobMessages.highPressureOffLegend),
      value: showTimeDetails ? hpOffTime : (hpOffTime / totalCalendarTime) * 100,
      color: 'data-green',
    });
    dataArray.push({
      key: 'ctrlOff',
      label: formatMessage(JobMessages.controllerOffLegend),
      value: showTimeDetails ? controllerOffTime : (controllerOffTime / totalCalendarTime) * 100,
      color: 'data-orange',
    });

    return dataArray;
  }, [
    highPressureRange,
    controllerOffTime,
    formatMessage,
    showTimeDetails,
    totalCalendarTime,
    filteredControllerOffRanges,
  ]);

  return (
    <IonCard>
      <IonCardHeader>
        <IonRow className="ion-justify-content-between ion-align-items-center">
          <DqText variant="h3">{title}</DqText>
          <IonButton fill="clear" color="primary" onClick={() => setHelpDialogOpen(true)}>
            <IonIcon slot="icon-only" icon={informationCircle} />
          </IonButton>
        </IonRow>
      </IonCardHeader>
      <IonCardContent>
        {chartInfo ? (
          <PieChart data={chartInfo} showTimeDetails={showTimeDetails} />
        ) : (
          <DqText>{formatMessage(JobMessages.noHighPressureData)}</DqText>
        )}
      </IonCardContent>
      <HighPressureModal isOpen={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
    </IonCard>
  );
}
